import React, { useState } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";

// NFL team primary brand colors (abbreviation -> {bg, text}). Logos carry trademark
// risk, so we render clean color-coded abbreviation pills instead.
export const TEAM_COLORS = {
  ARI: { bg: "#97233F", text: "#fff" }, ATL: { bg: "#A71930", text: "#fff" },
  BAL: { bg: "#241773", text: "#fff" }, BUF: { bg: "#00338D", text: "#fff" },
  CAR: { bg: "#0085CA", text: "#fff" }, CHI: { bg: "#0B162A", text: "#fff" },
  CIN: { bg: "#FB4F14", text: "#000" }, CLE: { bg: "#311D00", text: "#fff" },
  DAL: { bg: "#041E42", text: "#fff" }, DEN: { bg: "#FB4F14", text: "#fff" },
  DET: { bg: "#0076B6", text: "#fff" }, GB: { bg: "#203731", text: "#fff" },
  HOU: { bg: "#03202F", text: "#fff" }, IND: { bg: "#002C5F", text: "#fff" },
  JAX: { bg: "#006778", text: "#fff" }, KC: { bg: "#E31837", text: "#fff" },
  LV: { bg: "#000000", text: "#fff" }, LAC: { bg: "#0080C6", text: "#fff" },
  LAR: { bg: "#003594", text: "#fff" }, MIA: { bg: "#008E97", text: "#fff" },
  MIN: { bg: "#4F2683", text: "#fff" }, NE: { bg: "#002244", text: "#fff" },
  NO: { bg: "#D3BC8D", text: "#000" }, NYG: { bg: "#0B2265", text: "#fff" },
  NYJ: { bg: "#125740", text: "#fff" }, PHI: { bg: "#004C54", text: "#fff" },
  PIT: { bg: "#FFB612", text: "#000" }, SF: { bg: "#AA0000", text: "#fff" },
  SEA: { bg: "#002244", text: "#fff" }, TB: { bg: "#D50A0A", text: "#fff" },
  TEN: { bg: "#0C2340", text: "#fff" }, WAS: { bg: "#5A1414", text: "#fff" },
};

export function teamStyle(team) {
  const c = TEAM_COLORS[(team || "").toUpperCase()];
  return c ? { background: c.bg, color: c.text } : { background: "#1A1A1A", color: "#9CA3AF" };
}

export function TeamPill({ team, className = "" }) {
  if (!team) return null;
  const t = team.toUpperCase();
  return (
    <span data-testid={`team-pill-${t}`} style={teamStyle(t)}
      className={`inline-flex items-center justify-center text-[9px] font-bold tracking-wider rounded px-1.5 py-0.5 leading-none ${className}`}>
      {t}
    </span>
  );
}

export function PosRank({ rank, delta, className = "", onFilter }) {
  if (!rank) return null;
  const moved = delta != null && delta !== 0;
  const pos = (String(rank).match(/^[A-Za-z]+/) || [])[0] || "";
  const clickable = typeof onFilter === "function" && pos;
  return (
    <span className="inline-flex items-center gap-1">
      <span data-testid={`pos-rank-${rank}`}
        onClick={clickable ? (e) => { e.stopPropagation(); e.preventDefault(); onFilter(pos.toUpperCase()); } : undefined}
        role={clickable ? "button" : undefined}
        className={`inline-flex items-center justify-center text-[9px] font-mono font-bold tracking-wider rounded px-1.5 py-0.5 leading-none border border-[var(--accent)] text-[var(--accent)] ${clickable ? "cursor-pointer hover:bg-[var(--accent)] hover:text-black transition-colors" : ""} ${className}`}
        title={clickable ? `Filter to ${pos.toUpperCase()}` : "Dynasty Metrics positional rank by DME value"}>
        {rank}
      </span>
      {moved && (
        <span data-testid={`rank-delta-${rank}`}
          className={`inline-flex items-center text-[9px] font-mono font-bold leading-none ${delta > 0 ? "text-[var(--accent)]" : "text-red-400"}`}
          title={`${Math.abs(delta)} spot${Math.abs(delta) > 1 ? "s" : ""} ${delta > 0 ? "up" : "down"} vs 7 days ago`}>
          {delta > 0 ? <ArrowUp size={9} strokeWidth={3} /> : <ArrowDown size={9} strokeWidth={3} />}
          {Math.abs(delta)}
        </span>
      )}
    </span>
  );
}

function initials(name = "") {
  return name.split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function PlayerHeadshot({ playerId, name, team, size = 40, thumb = true, className = "" }) {
  const [err, setErr] = useState(false);
  const isReal = /^\d+$/.test(String(playerId || ""));
  const src = isReal
    ? `https://sleepercdn.com/content/nfl/players/${thumb ? "thumb/" : ""}${playerId}.jpg`
    : null;
  const dim = { width: size, height: size };

  if (!src || err) {
    return (
      <div data-testid="player-headshot-fallback" style={{ ...dim, ...teamStyle(team) }}
        className={`flex items-center justify-center rounded-full font-bold shrink-0 ${className}`}>
        <span style={{ fontSize: size * 0.34 }}>{initials(name)}</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={`${name} headshot`}
      loading="lazy"
      onError={() => setErr(true)}
      style={dim}
      className={`rounded-full object-cover object-top bg-[#1A1A1A] shrink-0 ${className}`}
      data-testid="player-headshot"
    />
  );
}
